import OneSignal from 'onesignal-cordova-plugin';
import ReactOneSignal from 'react-onesignal';
import { Capacitor } from '@capacitor/core';

const ONE_SIGNAL_APP_ID = 'ec94996d-6ad0-415b-ade0-11ef05833f01';
const DEFAULT_ROUTE = '/notificaciones';

let nativeInitialized = false;
let webInitialized = false;
let foregroundListener = null;
let clickListener = null;
let webForegroundListener = null;
let webClickListener = null;
let nativeStartPromise = null;

const isFunction = (value) => typeof value === 'function';

const waitForNativeBridge = async () => {
  if (Capacitor.getPlatform() === 'web') {
    return;
  }

  if (nativeStartPromise) {
    return nativeStartPromise;
  }

  nativeStartPromise = new Promise((resolve) => {
    let resolved = false;

    const finish = () => {
      if (resolved) {
        return;
      }

      resolved = true;
      resolve();
    };

    document.addEventListener('deviceready', finish, { once: true });

    window.setTimeout(finish, 900);
  });

  return nativeStartPromise;
};

export const normalizeOneSignalNotification = (notification) => {
  const additionalData = notification?.additionalData || {};
  const title = notification?.title || additionalData?.title || 'Nueva notificacion';
  const message = notification?.body || additionalData?.message || '';

  return {
    id:
      notification?.notificationId ||
      additionalData?.notificationId ||
      additionalData?.id ||
      `${title}-${message}`,
    notificationId: notification?.notificationId || additionalData?.notificationId || null,
    title,
    message,
    route: additionalData?.route || additionalData?.path || DEFAULT_ROUTE,
    launchURL: notification?.launchURL || additionalData?.launchURL || null,
    additionalData,
    rawPayload: notification?.rawPayload || null,
    source: 'onesignal'
  };
};

const removeNativeListeners = () => {
  if (foregroundListener) {
    OneSignal.Notifications.removeEventListener(
      'foregroundWillDisplay',
      foregroundListener
    );
    foregroundListener = null;
  }

  if (clickListener) {
    OneSignal.Notifications.removeEventListener('click', clickListener);
    clickListener = null;
  }
};

const removeWebListeners = () => {
  if (webForegroundListener) {
    ReactOneSignal.Notifications.removeEventListener(
      'foregroundWillDisplay',
      webForegroundListener
    );
    webForegroundListener = null;
  }

  if (webClickListener) {
    ReactOneSignal.Notifications.removeEventListener('click', webClickListener);
    webClickListener = null;
  }
};

const ensureNativeReady = async () => {
  if (nativeInitialized) {
    return;
  }

  await waitForNativeBridge();

  OneSignal.Debug.setLogLevel(6);
  OneSignal.initialize(ONE_SIGNAL_APP_ID);
  nativeInitialized = true;

  try {
    await OneSignal.Notifications.requestPermission(true);
  } catch (error) {
    console.warn('OneSignal: no se pudo solicitar permiso de notificaciones.', error);
  }
};

const attachNativeListeners = ({ onForegroundNotification, onNotificationClick }) => {
  removeNativeListeners();

  foregroundListener = (event) => {
    const notification = normalizeOneSignalNotification(event.getNotification());

    event.preventDefault();

    if (isFunction(onForegroundNotification)) {
      onForegroundNotification(notification);
    }
  };

  clickListener = (event) => {
    const notification = normalizeOneSignalNotification(event.notification);

    if (isFunction(onNotificationClick)) {
      onNotificationClick(notification, event.result);
    }
  };

  OneSignal.Notifications.addEventListener(
    'foregroundWillDisplay',
    foregroundListener
  );
  OneSignal.Notifications.addEventListener('click', clickListener);
};

const attachWebListeners = ({ onForegroundNotification, onNotificationClick }) => {
  removeWebListeners();

  webForegroundListener = (event) => {
    const notification = normalizeOneSignalNotification(event.notification);

    event.preventDefault();

    if (isFunction(onForegroundNotification)) {
      onForegroundNotification(notification);
    }
  };

  webClickListener = (event) => {
    const notification = normalizeOneSignalNotification(event.notification);

    if (isFunction(onNotificationClick)) {
      onNotificationClick(notification, event.result);
    }
  };

  ReactOneSignal.Notifications.addEventListener(
    'foregroundWillDisplay',
    webForegroundListener
  );
  ReactOneSignal.Notifications.addEventListener('click', webClickListener);
};

const setupWebNotifications = async () => {
  if (webInitialized) {
    return;
  }

  await ReactOneSignal.init({
    appId: ONE_SIGNAL_APP_ID,
    allowLocalhostAsSecureOrigin: true
  });

  webInitialized = true;
};

// — Vinculación usuario ↔ suscripción push (external_id) —
// La suscripción push (el "token" del dispositivo) ya existe desde que el
// usuario aceptó las notificaciones. login() NO crea un token nuevo ni pide
// permisos otra vez: solo etiqueta esa suscripción existente con el Id de
// nuestro usuario, para poder enviarle push individuales por external_id.
const waitMs = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const linkOneSignalUser = async (userId) => {
  if (!userId) return;
  const externalId = String(userId);
  const platform = Capacitor.getPlatform();
  const isWeb = platform === 'web';

  // Espera a que setupNotifications haya inicializado el SDK (máx ~10s).
  for (let i = 0; i < 20; i += 1) {
    if (isWeb ? webInitialized : nativeInitialized) break;
    await waitMs(500);
  }

  try {
    if (isWeb) {
      if (!webInitialized) return;
      await ReactOneSignal.login(externalId);
    } else {
      if (!nativeInitialized) return;
      OneSignal.login(externalId);
    }
    console.log(`OneSignal: usuario ${externalId} vinculado (${platform}).`);
  } catch (error) {
    console.warn('OneSignal: no se pudo vincular el usuario.', error);
  }
};

export const unlinkOneSignalUser = async () => {
  const platform = Capacitor.getPlatform();
  try {
    if (platform === 'web') {
      if (webInitialized) await ReactOneSignal.logout();
    } else if (nativeInitialized) {
      OneSignal.logout();
    }
  } catch (error) {
    console.warn('OneSignal: no se pudo desvincular el usuario.', error);
  }
};

export const clearNativeOneSignalNotifications = () => {
  const platform = Capacitor.getPlatform();

  if (platform !== 'android' && platform !== 'ios') {
    return false;
  }

  if (!nativeInitialized) {
    return false;
  }

  try {
    OneSignal.Notifications.clearAll();
    return true;
  } catch (error) {
    console.error('OneSignal: no se pudieron limpiar notificaciones.', error);
    return false;
  }
};

const setupNotifications = async (config = {}) => {
  const platform = Capacitor.getPlatform();

  try {
    if (platform === 'web') {
      await setupWebNotifications();
      attachWebListeners(config);
      return removeWebListeners;
    }

    if (platform !== 'android' && platform !== 'ios') {
      return removeNativeListeners;
    }

    await ensureNativeReady();
    attachNativeListeners(config);

    console.log(`OneSignal listo en ${platform}. Listener foreground registrado.`);

    return removeNativeListeners;
  } catch (error) {
    console.error('Error configurando OneSignal:', error);
    return removeNativeListeners;
  }
};

export default setupNotifications;
